// Fetches the SPA's runtime configuration from the server (GET /api/client-config)
// instead of baking any of it into the build. This lets one build deploy unchanged to
// every environment. See main.tsx: loadClientConfig() is awaited, and everything that
// depends on it (msal.ts) is imported only after it resolves, so getClientConfig()
// below is always safe to call synchronously by the time those modules run.

export type ClientConfig = {
  tenantId: string;
  clientId: string;
  apiScope: string;
};

const REQUIRED_KEYS = ["tenantId", "clientId", "apiScope"] as const;

async function fetchClientConfig(): Promise<ClientConfig> {
  const res = await fetch("/api/client-config");
  if (!res.ok) {
    throw new Error(`[client-config] Failed to load /api/client-config: ${res.status} ${res.statusText}`);
  }
  const config = (await res.json()) as Partial<ClientConfig>;
  const missing = REQUIRED_KEYS.filter((k) => !config[k]);
  if (missing.length) {
    throw new Error(`[client-config] /api/client-config is missing: ${missing.join(", ")}`);
  }
  return config as ClientConfig;
}

let cached: ClientConfig | undefined;
let loadPromise: Promise<ClientConfig> | undefined;

/** Fetches and caches the client config. Call once, before importing anything that needs it. */
export function loadClientConfig(): Promise<ClientConfig> {
  loadPromise ??= fetchClientConfig().then((config) => (cached = config));
  return loadPromise;
}

/** Synchronous accessor. Throws if loadClientConfig() hasn't resolved yet. */
export function getClientConfig(): ClientConfig {
  if (!cached) throw new Error("[client-config] Not loaded yet — call/await loadClientConfig() first.");
  return cached;
}
